import DOMPurify from 'dompurify';

// Matches sanitize-html's default tag allowlist. DOMPurify's defaults are much
// broader (img, style, form, input, SVG, etc.) which we don't need.
const ALLOWED_TAGS = [
  'address',
  'article',
  'aside',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'main',
  'nav',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'ul',
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'br',
  'cite',
  'code',
  'data',
  'dfn',
  'em',
  'i',
  'kbd',
  'mark',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
  'wbr',
  'caption',
  'col',
  'colgroup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
];

// Matches sanitize-html's default attribute allowlist:
// only href, name, target on <a> tags — nothing else.
const ALLOWED_ATTR = ['href', 'name', 'target'];

/**
 * Sanitize HTML using DOMPurify with a restrictive allowlist matching
 * sanitize-html's defaults. Strips img, style, form, input, SVG, script,
 * iframe, and most attributes (only href/name/target kept).
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS, ALLOWED_ATTR });
}

// cspell:ignore colspan rowspan
// Ember's `relaxedConfig` (Sanitize.js "relaxed"): basic formatting plus links, images and tables.
const RELAXED_ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'caption',
  'cite',
  'code',
  'col',
  'colgroup',
  'dd',
  'dl',
  'dt',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'q',
  'small',
  'strike',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const RELAXED_ALLOWED_ATTR = [
  'href',
  'title',
  'target',
  'rel',
  'cite',
  'span',
  'width',
  'align',
  'alt',
  'height',
  'src',
  'start',
  'type',
  'summary',
  'abbr',
  'axis',
  'colspan',
  'rowspan',
  'scope',
];

/**
 * Sanitize HTML the way Ember's SMS previews do: the "relaxed" allowlist, which
 * keeps `img` (a Design Studio SMS body renders its media as `<img>`) and
 * links, tables and basic formatting that `sanitizeHtml` strips.
 */
export function sanitizeRelaxedHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: RELAXED_ALLOWED_TAGS, ALLOWED_ATTR: RELAXED_ALLOWED_ATTR });
}
